import type { CounselRequestTermHistory } from './api'
import { TERMS_TYPE_CODE } from './constants'
import type { TermItem } from './types'

/**
 * 상담신청(/counsel-request) termsHistories payload 빌더 (INT-209)
 *
 * - sn 미전송: CRM 은 sn + type 동시 수신 시 sn 우선이라, ajung 기준 sn 을 남기면
 *   알뜰폰 상담이 들어가는 crm_amvno 테넌트에서 매핑에 실패해 약관 미동의로 저장된다.
 *   약관 sn 은 테넌트 스키마별 auto-increment PK 라 테넌트 간 공유 불가.
 * - 미동의 약관도 전송: 동의 항목만 filter 하지 않고 전체를 isAgree 로 구분해 거부 이력을 남긴다.
 * - sn 없는 항목은 제외: 화면 표시용으로 SDK 가 하드코딩하는 약관(상조 LIFECARE_TERMS)은
 *   sn·type 이 없어 CRM 이 매핑할 수 없다. type 이 아닌 sn 으로 거르는 이유 —
 *   API 약관의 type 이 비어 있어도 동의 이력은 보내야 하고, 그 판단은 CRM 이 한다.
 *   SDK 에서 조용히 누락시키면 동의 기록 자체가 사라진다.
 *
 * ⚠️ 이 함수를 쓰면 termsHistories 가 "동의한 약관"에서 "전체 약관"으로 의미가 바뀐다.
 *    마케팅 동의 여부를 termsHistories 로 계산하는 코드는 isCheck 가드가 필요하다.
 */
export const buildTermsHistories = (terms?: TermItem[] | null): CounselRequestTermHistory[] =>
  // terms 는 fetchTerms 결과라 실패 시 빈 배열이지만, 호스트가 넘긴 값이 섞일 수 있어 한 번 막는다.
  // 상담 제출 자체가 터지면 안 되므로 여기서 방어 (호출부 2곳에 각각 가드하는 것보다 안전)
  (Array.isArray(terms) ? terms : [])
    .filter(term => term.sn != null)
    .map(term => ({ division: term.division, type: term.type, isAgree: !!term.isCheck }))

/**
 * 마케팅 수신 동의 약관 판정 (INT-209)
 *
 * sn 하드코딩(31) 을 대체한다. sn 은 CRM 테넌트 스키마별 PK 라 amvno 에서 다른 약관을 가리키지만
 * type 은 테넌트 공통 codeId 라 안전하다. SDK 하드코딩 약관(상조)은 type 이 없어 자연히 제외된다.
 *
 * ⚠️ 동의 여부까지 보려면 isCheck 를 함께 확인해야 한다. termsHistories 는 미동의 약관도 포함한다.
 *    예: terms.some(term => term.isCheck && isMarketingTerm(term))
 */
export const isMarketingTerm = (term: TermItem): boolean =>
  term.type === TERMS_TYPE_CODE.MARKETING
